import { useState, useRef, useCallback } from "react";
import { Formik, Form, useFormikContext } from "formik";
import * as Yup from "yup";
import ReactSelect, { SingleValue } from "react-select";
import FormField from "../../../components/FormField";
import Button from "../../../components/Button";
import { Tooltip } from "../../../components/tooltip";
import { ShowToast } from "../../../components/toast";

import { useAvailableHotels, useOnboardHotel, useBrandSetup } from "../hooks";
import {
  OnboardHotelPayload,
  AvailableHotel,
  BookingEngineType,
  BOOKING_ENGINE_OPTIONS,
  PMS_TO_ENGINE,
  ENGINE_URL_PLACEHOLDERS,
} from "../types";
import { validateIconFile } from "../utils";

type HotelOption = {
  value: string;
  label: string;
  pmsType: string;
  hotel: AvailableHotel;
};

type EngineOption = { value: BookingEngineType; label: string };

function resolveEngineType(pmsType: string): BookingEngineType {
  return PMS_TO_ENGINE[pmsType?.toLowerCase()] || "CUSTOM";
}

function buildValidationSchema(engineType: BookingEngineType | undefined) {
  const base: Record<string, Yup.Schema> = {
    hotelId: Yup.string().required("Hotel is required"),
    bookingEngineBaseUrl: Yup.string()
      .matches(
        /^(https?:\/\/)?[\w-]+(\.[\w-]+)+([/?#].*)?$/,
        "Must be a valid URL"
      )
      .required("Booking engine URL is required"),
  };

  if (engineType === "AUTUMN") {
    base["bookingEngineConfig.hotelCode"] = Yup.string();
    base["bookingEngineConfig.pricingApiUrl"] = Yup.string();
  }

  if (engineType === "AUTUMN" || engineType === "CUSTOM") {
    base.checkInTime = Yup.string()
      .matches(/^\d{2}:\d{2}$/, "Must be HH:MM format")
      .required("Check-in time is required");
    base.checkOutTime = Yup.string()
      .matches(/^\d{2}:\d{2}$/, "Must be HH:MM format")
      .required("Check-out time is required");
  }

  if (engineType === "CUSTOM") {
    base["bookingEngineConfig.checkInParam"] = Yup.string();
    base["bookingEngineConfig.checkOutParam"] = Yup.string();
  }

  return Yup.object(base);
}

const initialValues: OnboardHotelPayload = {
  hotelId: "",
  bookingEngineBaseUrl: "",
  bookingEngineType: undefined,
  bookingEngineConfig: null,
  checkInTime: "",
  checkOutTime: "",
};

type SuccessData = {
  hotelName: string;
  configId: string;
};

function SectionHeader({
  step,
  title,
  description,
}: {
  step: number;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-white text-xs font-semibold flex items-center justify-center">
        {step}
      </span>
      <h3 className="text-base font-semibold text-gray-900">{title}</h3>
      <Tooltip id={`section-${step}-info`} className="!w-[260px]">
        <p className="text-xs">{description}</p>
      </Tooltip>
    </div>
  );
}

function ConfigInput({
  label,
  field,
  placeholder,
  badge,
}: {
  label: string;
  field: string;
  placeholder: string;
  badge?: string;
}) {
  const { values, setFieldValue } = useFormikContext<OnboardHotelPayload>();
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {badge && (
          <span className="text-xs bg-blue-100 text-blue-800 border border-blue-300 px-1.5 py-0.5 rounded ml-1">
            {badge}
          </span>
        )}
      </label>
      <input
        type="text"
        value={values.bookingEngineConfig?.[field] || ""}
        onChange={(e) =>
          setFieldValue("bookingEngineConfig", {
            ...values.bookingEngineConfig,
            [field]: e.target.value,
          })
        }
        className="w-full border border-neutral-400 rounded px-3 py-2 text-sm"
        placeholder={placeholder}
      />
    </div>
  );
}

function OnboardFormContent({
  hotels,
  engineType,
  onEngineChange,
  selectedHotel,
  setSelectedHotel,
  iconPreview,
  onIconSelect,
  onIconClear,
}: {
  hotels: AvailableHotel[];
  engineType: BookingEngineType | undefined;
  onEngineChange: (engine: BookingEngineType) => void;
  selectedHotel: AvailableHotel | null;
  setSelectedHotel: (h: AvailableHotel | null) => void;
  iconPreview: string | null;
  onIconSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onIconClear: () => void;
}) {
  const { values, setFieldValue, isValid, isSubmitting } =
    useFormikContext<OnboardHotelPayload>();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hotelOptions = hotels.map((h) => ({
    value: h.hotelId,
    label: `${h.hotelName}${h.city ? ` — ${h.city}` : ""}`,
    pmsType: h.pmsType,
    hotel: h,
  }));

  const handleHotelChange = (selected: SingleValue<HotelOption>) => {
    if (!selected) {
      setFieldValue("hotelId", "");
      setSelectedHotel(null);
      return;
    }
    setFieldValue("hotelId", selected.value);
    setSelectedHotel(selected.hotel);
    setFieldValue("bookingEngineType", undefined);
    setFieldValue("bookingEngineConfig", null);
    setFieldValue("checkInTime", "");
    setFieldValue("checkOutTime", "");
    onEngineChange(resolveEngineType(selected.pmsType));
  };

  const handleEngineOverride = (selected: SingleValue<EngineOption>) => {
    const newEngine = selected?.value || undefined;
    setFieldValue("bookingEngineType", newEngine || undefined);
    setFieldValue("bookingEngineConfig", null);
    setFieldValue("checkInTime", "");
    setFieldValue("checkOutTime", "");
    if (newEngine) {
      onEngineChange(newEngine);
    } else if (selectedHotel) {
      onEngineChange(resolveEngineType(selectedHotel.pmsType));
    }
  };

  const urlPlaceholder = engineType
    ? ENGINE_URL_PLACEHOLDERS[engineType]
    : "https://...";

  const showCheckTimes = engineType === "AUTUMN" || engineType === "CUSTOM";

  return (
    <Form className="space-y-5">
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
        <SectionHeader
          step={1}
          title="Select a Hotel"
          description="Choose from hotels connected to your PMS that aren't on Google Metasearch yet."
        />
        <div>
          <ReactSelect
            options={hotelOptions}
            value={
              hotelOptions.find((opt) => opt.value === values.hotelId) || null
            }
            onChange={handleHotelChange}
            isSearchable
            placeholder="Type to search hotels..."
            isClearable
            formatOptionLabel={(opt: HotelOption) => (
              <div className="flex items-center gap-2">
                <span>{opt.label}</span>
                {opt.pmsType && (
                  <span className="text-xs bg-gray-100 text-gray-700 border border-gray-300 px-1.5 py-0.5 rounded">
                    {opt.pmsType}
                  </span>
                )}
              </div>
            )}
          />
        </div>

        {selectedHotel && (
          <div className="mt-3 p-3 bg-[#FAFAFA] rounded-lg text-sm text-gray-600 flex flex-wrap gap-x-6 gap-y-1">
            <span>
              <span className="text-gray-400">Engine:</span>{" "}
              <span className="font-medium">
                {BOOKING_ENGINE_OPTIONS.find((o) => o.value === engineType)
                  ?.label || engineType}
              </span>
            </span>
            {selectedHotel.currencyCode && (
              <span>
                <span className="text-gray-400">Currency:</span>{" "}
                <span className="font-medium">
                  {selectedHotel.currencyCode}
                </span>
              </span>
            )}
            {selectedHotel.state && (
              <span>
                <span className="text-gray-400">State:</span>{" "}
                <span className="font-medium">{selectedHotel.state}</span>
              </span>
            )}
          </div>
        )}
      </div>

      {values.hotelId && (
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
          <SectionHeader
            step={2}
            title="Configure Booking Engine"
            description="Tell us where guests should land when they click your Google listing."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Engine URL
                </label>
                <input
                  type="text"
                  name="bookingEngineBaseUrl"
                  value={values.bookingEngineBaseUrl}
                  onChange={(e) => setFieldValue("bookingEngineBaseUrl", e.target.value)}
                  className="w-full border border-neutral-400 rounded px-3 py-2 text-sm"
                  placeholder={urlPlaceholder}
                />
              </div>
            </div>

            {engineType === "AUTUMN" && (
              <>
                <ConfigInput label="Hotel Code" field="hotelCode" placeholder="Hotel code" />
                <div>
                  <ConfigInput
                    label="Pricing API URL"
                    field="pricingApiUrl"
                    placeholder="https://api.yourhotel.com/pricing"
                  />
                  <div className="flex items-center gap-1.5 mt-1">
                    <Tooltip id="pricing-api-info" className="!w-[220px]">
                      <p className="text-xs">
                        Used by Google to verify your prices are accurate
                      </p>
                    </Tooltip>
                  </div>
                </div>
              </>
            )}

            {engineType === "CUSTOM" && (
              <>
                <ConfigInput label="Check-in URL Parameter" field="checkInParam" placeholder="e.g. checkin" />
                <ConfigInput label="Check-out URL Parameter" field="checkOutParam" placeholder="e.g. checkout" />
                <div className="flex items-center gap-1.5 -mt-2 md:col-span-2">
                  <Tooltip id="url-params-info" className="!w-[220px]">
                    <p className="text-xs">
                      The URL parameter names your booking engine uses for dates
                    </p>
                  </Tooltip>
                </div>

                <ConfigInput label="Room Type Param" field="roomTypeParam" placeholder="e.g. room_type" badge="recommended" />
                <ConfigInput label="Rate Param" field="rateParam" placeholder="e.g. rate" badge="recommended" />
                <ConfigInput label="Adults Param" field="adultsParam" placeholder="e.g. adults" badge="recommended" />
                <div className="flex items-center gap-1.5 md:col-span-2">
                  <Tooltip id="room-rate-params-info" className="!w-[220px]">
                    <p className="text-xs">
                      Without these, booking links won't include room or rate
                      details
                    </p>
                  </Tooltip>
                </div>
              </>
            )}

            {showCheckTimes && (
              <>
                <FormField
                  label="Check-in Time"
                  name="checkInTime"
                  type="text"
                  placeholder="15:00"
                />
                <FormField
                  label="Check-out Time"
                  name="checkOutTime"
                  type="text"
                  placeholder="11:00"
                />
              </>
            )}
          </div>
        </div>
      )}

      {values.hotelId && (
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-5">
          <SectionHeader
            step={3}
            title="Hotel Logo (Optional)"
            description="Upload a square PNG logo (72–1200px). A brand will be created automatically on Google."
          />
          {iconPreview ? (
            <div className="flex items-center gap-4 p-3 bg-[#FAFAFA] rounded-lg border border-[#E9E9EB]">
              <img
                src={iconPreview}
                alt="Logo preview"
                className="w-12 h-12 rounded border border-gray-200 object-contain"
              />
              <span className="flex-1 text-sm text-gray-600">Logo ready</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  onIconClear();
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
              >
                Remove
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors"
            >
              <svg
                className="w-8 h-8 mx-auto mb-2 text-gray-400"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="m21 15-5-5L5 21" />
              </svg>
              <span className="text-sm text-gray-500">
                Click to upload a square PNG (72–1200px)
              </span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png"
            className="hidden"
            onChange={onIconSelect}
          />
        </div>
      )}

      {values.hotelId && (
        <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB]">
          <button
            type="button"
            onClick={() => setAdvancedOpen(!advancedOpen)}
            className="w-full flex items-center justify-between p-4 text-sm font-medium text-gray-500 hover:bg-gray-50 rounded-lg"
          >
            <span>Advanced Settings</span>
            <span className="text-xs">{advancedOpen ? "▲" : "▼"}</span>
          </button>
          {advancedOpen && (
            <div className="px-5 pb-5 border-t border-[#E9E9EB] pt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Engine Type Override
                </label>
                <ReactSelect
                  options={BOOKING_ENGINE_OPTIONS}
                  value={
                    values.bookingEngineType
                      ? BOOKING_ENGINE_OPTIONS.find(
                          (opt) => opt.value === values.bookingEngineType
                        )
                      : null
                  }
                  onChange={handleEngineOverride}
                  isClearable
                  placeholder={`Auto: ${
                    selectedHotel
                      ? BOOKING_ENGINE_OPTIONS.find(
                          (o) =>
                            o.value ===
                            resolveEngineType(selectedHotel.pmsType)
                        )?.label || "Custom"
                      : "—"
                  }`}
                />
                <div className="flex items-center gap-1.5 mt-1">
                  <Tooltip id="engine-override-info" className="!w-[220px]">
                    <p className="text-xs">
                      Only change this if the auto-detected engine type is wrong
                    </p>
                  </Tooltip>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {values.hotelId && (
        <div className="flex justify-end">
          <Button type="submit" loading={isSubmitting} disabled={!isValid}>
            Onboard Hotel
          </Button>
        </div>
      )}
    </Form>
  );
}

export function OnboardContent({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel?: () => void;
}) {
  const { data: hotels, isLoading } = useAvailableHotels();
  const onboardMutation = useOnboardHotel();
  const brandSetup = useBrandSetup();
  const [effectiveEngine, setEffectiveEngine] = useState<
    BookingEngineType | undefined
  >();
  const [selectedHotel, setSelectedHotel] = useState<AvailableHotel | null>(
    null
  );
  const [iconPreview, setIconPreview] = useState<string | null>(null);
  const [iconData, setIconData] = useState<string | null>(null);
  const [successData, setSuccessData] = useState<SuccessData | null>(null);

  const handleIconSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      validateIconFile(
        file,
        (base64, previewUrl) => {
          setIconData(base64);
          setIconPreview(previewUrl);
        },
        (message) => ShowToast({ type: "error", message }),
      );
    },
    []
  );

  const handleIconClear = useCallback(() => {
    setIconData(null);
    setIconPreview(null);
  }, []);

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-12 text-center">
        <p className="text-gray-500">Loading available hotels...</p>
      </div>
    );
  }

  if (successData) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-[#E9E9EB] p-10 text-center max-w-lg mx-auto">
        <svg
          className="w-12 h-12 mx-auto mb-4 text-primary"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <polyline points="22 4 12 14.01 9 11.01" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {successData.hotelName} is onboarded
        </h3>
        <p className="text-sm text-gray-500 mb-6">
          The hotel has been registered with Google Metasearch. Here's what
          happens next:
        </p>
        <div className="text-sm text-gray-600 space-y-2 text-left bg-[#FAFAFA] rounded-lg p-4 mb-6">
          <div className="flex items-start gap-2">
            <span className="text-green-500 mt-0.5">✓</span>
            <span>Tax policies will auto-sync from PMS data</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-green-500 mt-0.5">✓</span>
            <span>Room-rate mappings were auto-generated</span>
          </div>
          <div className="flex items-start gap-2">
            <span className="text-yellow-500 mt-0.5">○</span>
            <span>
              Feed is <strong>off</strong> by default — enable it from the
              Dashboard after reviewing the config
            </span>
          </div>
        </div>
        <Button
          onClick={() => {
            setSuccessData(null);
            onSuccess();
          }}
        >
          Go to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
        >
          <span>&larr;</span> Back to Dashboard
        </button>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={buildValidationSchema(effectiveEngine)}
        enableReinitialize={false}
        isInitialValid={false}
        onSubmit={(values, actions) => {
          const payload: OnboardHotelPayload = {
            hotelId: values.hotelId,
            bookingEngineBaseUrl: values.bookingEngineBaseUrl,
          };

          if (values.bookingEngineType) {
            payload.bookingEngineType = values.bookingEngineType;
          }

          if (values.bookingEngineConfig) {
            const config = { ...values.bookingEngineConfig };
            if (typeof config.aeunits === "string" && config.aeunits.trim()) {
              try {
                config.aeunits = JSON.parse(config.aeunits);
              } catch {
                actions.setFieldError(
                  "bookingEngineConfig",
                  "AE Units must be valid JSON"
                );
                actions.setSubmitting(false);
                return;
              }
            }
            const cleaned = Object.fromEntries(
              Object.entries(config).filter(
                ([, v]) => v !== "" && v !== undefined && v !== null
              )
            );
            if (Object.keys(cleaned).length > 0) {
              payload.bookingEngineConfig = cleaned;
            }
          }

          if (values.checkInTime) payload.checkInTime = values.checkInTime;
          if (values.checkOutTime) payload.checkOutTime = values.checkOutTime;

          onboardMutation.mutate(payload, {
            onSuccess: (data) => {
              const result = data as { configId?: string; id?: string } | undefined;
              const configId = result?.configId || result?.id || "";
              if (iconData && configId) {
                const previewToStore = iconPreview;
                brandSetup.mutate(
                  { configId, data: { imageBase64: iconData } },
                  {
                    onSuccess: (res) => {
                      const result = res as { brandId?: string } | undefined;
                      if (result?.brandId && previewToStore) {
                        try {
                          const key = "metasearch-brand-icon-previews";
                          const previews = JSON.parse(localStorage.getItem(key) || "{}");
                          previews[result.brandId] = previewToStore;
                          localStorage.setItem(key, JSON.stringify(previews));
                        } catch { /* quota */ }
                      }
                    },
                    onError: () => {
                      ShowToast({
                        type: "error",
                        message:
                          "Hotel onboarded but brand setup failed. Set it up from the dashboard.",
                      });
                    },
                  }
                );
              }
              setSuccessData({
                hotelName: selectedHotel?.hotelName || values.hotelId,
                configId,
              });
              handleIconClear();
              actions.resetForm();
            },
            onSettled: () => actions.setSubmitting(false),
          });
        }}
      >
        <OnboardFormContent
          hotels={hotels || []}
          engineType={effectiveEngine}
          onEngineChange={setEffectiveEngine}
          selectedHotel={selectedHotel}
          setSelectedHotel={setSelectedHotel}
          iconPreview={iconPreview}
          onIconSelect={handleIconSelect}
          onIconClear={handleIconClear}
        />
      </Formik>
    </div>
  );
}
